import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertCircle,
	ArrowLeft,
	Calendar,
	Edit,
	FileText,
	Heart,
	Image,
	Plus,
	Search,
	Trash2,
	User,
	UserCheck,
	Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute(
	"/_auth/app/_app/help/user-guide/customer-management",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();

	return (
		<div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6 sm:space-y-8">
			{/* Mobile Back Button */}
			<Link
				to="/app/help/user-guide"
				className="md:hidden flex items-center gap-2 text-primary"
			>
				<ArrowLeft className="w-4 h-4" />
				{t("common.back")}
			</Link>

			{/* Header */}
			<div className="mb-6 sm:mb-8">
				<div className="flex items-center space-x-3 mb-4">
					<div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-50 rounded-lg flex items-center justify-center shrink-0">
						<UserCheck className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
					</div>
					<div className="min-w-0">
						<h1 className="text-xl sm:text-3xl font-bold text-base-content">
							{t("help.customerManagement")}
						</h1>
						<p className="text-sm sm:text-lg text-base-content/70">
							{t("help.customerManagementDescription")}
						</p>
					</div>
				</div>
			</div>

			{/* Overview */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Users className="h-5 w-5" />
						<span>{t("help.customerOverview")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerOverviewDescription")}
					</p>
					<div className="bg-info/10 border border-info/20 rounded-lg p-4">
						<div className="flex items-start space-x-3">
							<AlertCircle className="h-5 w-5 text-info mt-0.5" />
							<div>
								<h4 className="font-medium text-info">
									{t("help.customerKeyBenefits")}
								</h4>
								<ul className="text-sm text-info/80 mt-2 space-y-1">
									<li>• {t("help.customerBenefit1")}</li>
									<li>• {t("help.customerBenefit2")}</li>
									<li>• {t("help.customerBenefit3")}</li>
									<li>• {t("help.customerBenefit4")}</li>
								</ul>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Accessing Customer Management */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<User className="h-5 w-5" />
						<span>{t("help.customerAccessing")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerAccessingDescription")}
					</p>
					<ol className="list-decimal list-inside space-y-2 text-base-content/80">
						<li>{t("help.customerAccessingStep1")}</li>
						<li>{t("help.customerAccessingStep2")}</li>
						<li>{t("help.customerAccessingStep3")}</li>
					</ol>
					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerImagePlaceholder1")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Customer List Overview */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Users className="h-5 w-5" />
						<span>{t("help.customerListOverview")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerListDescription")}
					</p>

					<div className="grid md:grid-cols-2 gap-4">
						<div className="space-y-3">
							<h4 className="font-medium text-base-content">
								{t("help.customerSearchFilter")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.customerSearchFeature1")}</li>
								<li>• {t("help.customerSearchFeature2")}</li>
								<li>• {t("help.customerSearchFeature3")}</li>
							</ul>
						</div>
						<div className="space-y-3">
							<h4 className="font-medium text-base-content">
								{t("help.customerPagination")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.customerPaginationFeature1")}</li>
								<li>• {t("help.customerPaginationFeature2")}</li>
								<li>• {t("help.customerPaginationFeature3")}</li>
							</ul>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerImagePlaceholder2")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Adding New Customers */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Plus className="h-5 w-5" />
						<span>{t("help.customerAddingNew")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerAddingDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerStep1")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.customerStep1Detail1")}</li>
								<li>{t("help.customerStep1Detail2")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerStep2")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div>
									<h5 className="font-medium text-sm text-base-content mb-2">
										{t("help.customerBasicInfo")}
									</h5>
									<ul className="text-sm text-base-content/70 space-y-1">
										<li>• {t("help.customerBasicInfo1")}</li>
										<li>• {t("help.customerBasicInfo2")}</li>
										<li>• {t("help.customerBasicInfo3")}</li>
										<li>• {t("help.customerBasicInfo4")}</li>
										<li>• {t("help.customerBasicInfo5")}</li>
										<li>• {t("help.customerBasicInfo6")}</li>
									</ul>
								</div>
								<div>
									<h5 className="font-medium text-sm text-base-content mb-2">
										{t("help.customerIdentification")}
									</h5>
									<ul className="text-sm text-base-content/70 space-y-1">
										<li>• {t("help.customerIdentification1")}</li>
										<li>• {t("help.customerIdentification2")}</li>
										<li>• {t("help.customerIdentification3")}</li>
									</ul>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerStep3")}
							</h4>
							<div className="bg-success/10 border border-success/20 rounded-lg p-4">
								<div className="flex items-start space-x-3">
									<AlertCircle className="h-5 w-5 text-success mt-0.5" />
									<div>
										<h5 className="font-medium text-success">
											{t("help.customerSmartAddressLookup")}
										</h5>
										<p className="text-sm text-success/80 mt-1">
											{t("help.customerSmartAddressDescription")}
										</p>
									</div>
								</div>
							</div>
							<ul className="text-sm text-base-content/70 space-y-1 mt-3">
								<li>• {t("help.customerAddressField1")}</li>
								<li>• {t("help.customerAddressField2")}</li>
								<li>• {t("help.customerAddressField3")}</li>
								<li>• {t("help.customerAddressField4")}</li>
								<li>• {t("help.customerAddressField5")}</li>
								<li>• {t("help.customerAddressField6")}</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerStep4")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.customerAdditionalInfo1")}</li>
								<li>• {t("help.customerAdditionalInfo2")}</li>
								<li>• {t("help.customerAdditionalInfo3")}</li>
							</ul>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerImagePlaceholder3")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Searching and Finding Customers */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Search className="h-5 w-5" />
						<span>{t("help.customerSearching")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerSearchingDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerSearchCapabilities")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.customerSearchCapability1")}</li>
								<li>• {t("help.customerSearchCapability2")}</li>
								<li>• {t("help.customerSearchCapability3")}</li>
								<li>• {t("help.customerSearchCapability4")}</li>
								<li>• {t("help.customerSearchCapability5")}</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerSearchTips")}
							</h4>
							<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
								<div className="flex items-start space-x-3">
									<AlertCircle className="h-5 w-5 text-warning mt-0.5" />
									<div>
										<h5 className="font-medium text-warning">
											{t("help.customerProTips")}
										</h5>
										<ul className="text-sm text-warning/80 mt-2 space-y-1">
											<li>• {t("help.customerSearchTip1")}</li>
											<li>• {t("help.customerSearchTip2")}</li>
											<li>• {t("help.customerSearchTip3")}</li>
											<li>• {t("help.customerSearchTip4")}</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerImagePlaceholder4")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Editing Customer Information */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Edit className="h-5 w-5" />
						<span>{t("help.customerEditing")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerEditingDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerAccessingEditMode")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.customerEditStep1")}</li>
								<li>{t("help.customerEditStep2")}</li>
								<li>{t("help.customerEditStep3")}</li>
								<li>{t("help.customerEditStep4")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerEditFeatures")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.customerEditFeature1")}</li>
								<li>• {t("help.customerEditFeature2")}</li>
								<li>• {t("help.customerEditFeature3")}</li>
								<li>• {t("help.customerEditFeature4")}</li>
								<li>• {t("help.customerEditFeature5")}</li>
							</ul>
						</div>

						<div className="bg-info/10 border border-info/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertCircle className="h-5 w-5 text-info mt-0.5" />
								<div>
									<h5 className="font-medium text-info">
										{t("help.customerPermissionRequirements")}
									</h5>
									<p className="text-sm text-info/80 mt-1">
										{t("help.customerPermissionDescription")}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerImagePlaceholder5")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Customer Details and Navigation */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<User className="h-5 w-5" />
						<span>{t("help.customerDetailsNavigation")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerDetailsDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerAvailableTabs")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-3">
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<User className="h-4 w-4 mr-2" />
											{t("help.customerProfileTab")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerProfileTabDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Heart className="h-4 w-4 mr-2" />
											{t("help.customerMedicalRecordsTab")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerMedicalRecordsTabDescription")}
										</p>
									</div>
								</div>
								<div className="space-y-3">
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<Calendar className="h-4 w-4 mr-2" />
											{t("help.customerEnrollmentsTab")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerEnrollmentsTabDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content flex items-center">
											<FileText className="h-4 w-4 mr-2" />
											{t("help.customerPaymentsTab")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPaymentsTabDescription")}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerImagePlaceholder6")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Medical Records Management */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Heart className="h-5 w-5" />
						<span>{t("help.customerMedicalRecordsManagement")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerMedicalRecordsDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerRecordTypesAvailable")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerAnamnesis")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerAnamnesisDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPhysicalEvaluation")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPhysicalEvaluationDescription")}
										</p>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerPeriodicEvaluation")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerPeriodicEvaluationDescription")}
										</p>
									</div>
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.customerTreatmentPlans")}
										</h5>
										<p className="text-sm text-base-content/70">
											{t("help.customerTreatmentPlansDescription")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerAddingMedicalRecords")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.customerMedicalStep1")}</li>
								<li>{t("help.customerMedicalStep2")}</li>
								<li>{t("help.customerMedicalStep3")}</li>
								<li>{t("help.customerMedicalStep4")}</li>
								<li>{t("help.customerMedicalStep5")}</li>
								<li>{t("help.customerMedicalStep6")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerViewingManagingRecords")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.customerMedicalFeature1")}</li>
								<li>• {t("help.customerMedicalFeature2")}</li>
								<li>• {t("help.customerMedicalFeature3")}</li>
								<li>• {t("help.customerMedicalFeature4")}</li>
								<li>• {t("help.customerMedicalFeature5")}</li>
							</ul>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.customerImagePlaceholder7")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Customer Deletion */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Trash2 className="h-5 w-5" />
						<span>{t("help.customerDeletion")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.customerDeletionDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.customerDeletionProcess")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.customerDeletionStep1")}</li>
								<li>{t("help.customerDeletionStep2")}</li>
								<li>{t("help.customerDeletionStep3")}</li>
								<li>{t("help.customerDeletionStep4")}</li>
							</ol>
						</div>

						<div className="bg-error/10 border border-error/20 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertCircle className="h-5 w-5 text-error mt-0.5" />
								<div>
									<h5 className="font-medium text-error">
										{t("help.customerImportantConsiderations")}
									</h5>
									<ul className="text-sm text-error/80 mt-2 space-y-1">
										<li>• {t("help.customerDeletionWarning1")}</li>
										<li>• {t("help.customerDeletionWarning2")}</li>
										<li>• {t("help.customerDeletionWarning3")}</li>
										<li>• {t("help.customerDeletionWarning4")}</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
